import React, { useState, useEffect } from 'react';
import { supabaseAdmin } from '../../lib/supabase';
import PageHeader from '../Common/PageHeader';
import Button from '../Common/Button';
import './ComprehensiveClassroomDashboard.css';

const ComprehensiveClassroomDashboard = ({ tenantId, onClassClick, onTabChange, onSidebarClose }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [viewMode, setViewMode] = useState('dashboard'); // 'dashboard', 'class', 'student'
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('All');
  const [sortBy, setSortBy] = useState('name'); // 'name', 'student_count', 'class_id'
  
  // Utility function to detect mobile devices
  const isMobileDevice = () => window.innerWidth <= 768;
  
  // Function to close sidebar on mobile devices
  const closeSidebarIfMobile = () => {
    if (onSidebarClose && isMobileDevice()) {
      console.log('📱 Mobile device detected, closing sidebar');
      onSidebarClose();
    } else {
      console.log('🖥️ Desktop device or no sidebar close callback, keeping sidebar open');
    }
  };
  
  // Add window resize listener for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      // Force re-render when window is resized
      // This ensures mobile detection is always current
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Sample subjects data - you can replace this with real data from your database
  const subjects = [
    { name: 'Biology', teacherCount: 8 },
    { name: 'Chemistry', teacherCount: 7 },
    { name: 'Chinese', teacherCount: 9 },
    { name: 'Economics', teacherCount: 9 },
    { name: 'English', teacherCount: 10 },
    { name: 'Geography', teacherCount: 10 },
    { name: 'History', teacherCount: 8 },
    { name: 'Mathematics', teacherCount: 12 },
    { name: 'Physics', teacherCount: 6 },
    { name: 'Physical Education', teacherCount: 5 }
  ];

  // Handle subject click
  const handleSubjectClick = (subject) => {
    // You can implement subject-specific functionality here
    // For example, filter teachers by subject, show subject details, etc.
  };

  // Navigation handlers for action buttons
  const handleAttendanceClick = () => {
    // Navigate to attendance management page
    onTabChange('attendance', { source: 'class-dashboard', returnTo: 'teacher-directory' });
  };

  const handlePaymentClick = () => {
    // Navigate to payment records page
    onTabChange('payment', { source: 'class-dashboard', returnTo: 'teacher-directory' });
  };

  const handleSchoolActivitiesClick = () => {
    // Navigate to school activities calendar
    onTabChange('school-event', { source: 'class-dashboard', returnTo: 'teacher-directory' });
  };

  const handleECAClick = () => {
    // Navigate to ECA page
    onTabChange('eca', { source: 'class-dashboard', returnTo: 'teacher-directory' });
  };

  useEffect(() => {
    fetchDashboardData();
  }, [tenantId]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      const effectiveTenantId = tenantId || 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3';
      
      // Test if Supabase client is working
      if (!supabaseAdmin || typeof supabaseAdmin.from !== 'function') {
        throw new Error('Supabase client not initialized');
      }
      
      // Test Supabase connection first with a simple query
      try {
        const { data: testData, error: testError } = await supabaseAdmin
          .from('tenants')
          .select('id')
          .limit(1);
        
        if (testError) {
          throw new Error(`Connection test failed: ${testError.message}`);
        }
        
      } catch (testErr) {
        throw new Error(`Supabase connection failed: ${testErr.message}`);
      }
      
      // Proceed with tenant lookup
      const { data: tenantData, error: tenantError } = await supabaseAdmin
        .from('tenants')
        .select('*')
        .eq('id', effectiveTenantId);

      if (tenantError) {
        throw new Error(`Tenant query failed: ${tenantError.message}`);
      }

      if (!tenantData || tenantData.length === 0) {
        throw new Error('Tenant not found');
      }

      const tenant = tenantData[0];
      
      // Fetch classes for this tenant
      const { data: classesData, error: classesError } = await supabaseAdmin
        .from('saas_classes')
        .select('*')
        .eq('tenant_id', effectiveTenantId)
        .order('class_id');

      if (classesError) {
        throw new Error(`Classes query failed: ${classesError.message}`);
      }

      // Fetch teachers for this tenant with class assignments
      const { data: teachersData, error: teachersError } = await supabaseAdmin
        .from('saas_teachers')
        .select('*')
        .eq('tenant_id', effectiveTenantId);

      if (teachersError) {
        throw new Error(`Teachers query failed: ${teachersError.message}`);
      }

      // Fetch students for this tenant
      const { data: studentsData, error: studentsError } = await supabaseAdmin
        .from('saas_students')
        .select('*')
        .eq('tenant_id', effectiveTenantId);

      if (studentsError) {
        throw new Error(`Students query failed: ${studentsError.message}`);
      }

      // Build dashboard data
      const dashboard = {
        tenant: {
          id: tenant.id,
          name: tenant.name,
          address: tenant.address
        },
        classes: classesData || [],
        teachers: teachersData || [],
        students: studentsData || [],
        stats: {
          totalClasses: classesData?.length || 0,
          totalStudents: studentsData?.length || 0,
          totalTeachers: teachersData?.length || 0,
          classesWithTeachers: 0
        }
      };

      // Calculate student count for each class
      if (classesData && studentsData) {
        dashboard.classes = dashboard.classes.map(cls => {
          const classStudents = studentsData.filter(student => student.class_id === cls.class_id);
          return {
            ...cls,
            student_count: classStudents.length
          };
        });
      }

      // Calculate classes with teachers and add teacher information to classes
      if (classesData && teachersData) {
        dashboard.stats.classesWithTeachers = classesData.filter(cls => {
          return teachersData.some(teacher => 
            teacher.class_master === cls.id || teacher.class_vice_master === cls.id
          );
        }).length;

        // Add teacher information to each class
        dashboard.classes = dashboard.classes.map(cls => {
          const classMaster = teachersData.find(teacher => teacher.class_master === cls.id);
          const viceMaster = teachersData.find(teacher => teacher.class_vice_master === cls.id);
          
          return {
            ...cls,
            class_master: classMaster ? {
              id: classMaster.id,
              name: classMaster.name,
              english_name: classMaster.english_name,
              department: classMaster.department,
              position: classMaster.position
            } : null,
            class_vice_master: viceMaster ? {
              id: viceMaster.id,
              name: viceMaster.name,
              english_name: viceMaster.english_name,
              department: viceMaster.department,
              position: viceMaster.position
            } : null
          };
        });
      }

      setDashboardData(dashboard);
      setError(null);
      
    } catch (err) {
      setError(err.message);
      setDashboardData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleClassClick = (classData) => {
    setSelectedClass(classData);
    setViewMode('class-detail');
    
    // Close sidebar on mobile devices when a class is selected
    closeSidebarIfMobile();
  };

  const handleBackToOverview = () => {
    setSelectedClass(null);
    setViewMode('overview');
  };

  const handleBackToClassList = () => {
    setViewMode('class-detail');
  };

  const getFilteredAndSortedClasses = () => {
    if (!dashboardData?.classes) {
      return [];
    }

    let filtered = dashboardData.classes.filter(cls => {
      // Add null checks to prevent toLowerCase errors
      const className = cls.name || cls.class_name || '';
      const classId = cls.class_id || '';
      
      const matchesSearch = className.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           classId.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesDepartment = filterDepartment === 'All' || 
        (cls.class_master?.department === filterDepartment) ||
        (cls.class_vice_master?.department === filterDepartment);
      
      return matchesSearch && matchesDepartment;
    });

    // Sort classes
    filtered.sort((a, b) => {
      const aName = a.name || a.class_name || '';
      const bName = b.name || b.class_name || '';
      const aId = a.class_id || '';
      const bId = b.class_id || '';
      
      switch (sortBy) {
        case 'name':
          return aName.localeCompare(bName);
        case 'student_count':
          // Calculate student count if not available
          const aCount = a.student_count || 0;
          const bCount = b.student_count || 0;
          return bCount - aCount;
        case 'class_id':
          return aId.localeCompare(bId);
        default:
          return 0;
      }
    });

    return filtered;
  };

  const getUniqueDepartments = () => {
    if (!dashboardData?.classes) return ['All'];
    
    const departments = new Set();
    dashboardData.classes.forEach(cls => {
      // Add null checks for class_master and class_vice_master
      if (cls.class_master && typeof cls.class_master === 'object' && cls.class_master.department) {
        departments.add(cls.class_master.department);
      }
      if (cls.class_vice_master && typeof cls.class_vice_master === 'object' && cls.class_vice_master.department) {
        departments.add(cls.class_vice_master.department);
      }
    });
    
    return ['All', ...Array.from(departments).sort()];
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>載入課室儀表板中...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <p>錯誤: {error}</p>
        <button onClick={fetchDashboardData}>重試</button>
      </div>
    );
  }

  if (viewMode === 'class-detail' && selectedClass) {
    return (
      <ClassDetailView 
        classData={selectedClass}
        onBack={handleBackToOverview}
        onViewStudents={() => {
          setViewMode('student-detail');
          // Close sidebar on mobile devices when viewing students
          closeSidebarIfMobile();
        }}
      />
    );
  }

  if (viewMode === 'student-detail' && selectedClass) {
    return (
      <StudentDetailView 
        classData={selectedClass}
        onBack={handleBackToClassList}
        tenantId={tenantId}
      />
    );
  }

  const filteredClasses = getFilteredAndSortedClasses();
  const departments = getUniqueDepartments();

  return (
    <div className="comprehensive-dashboard">
      {/* Dashboard Header */}
      <div className="dashboard-header">
        <div className="header-main">
          <h1>Welcome back, Teacher!</h1>
          <p>{dashboardData?.tenant?.name || 'MyClass Real School'} - Manage your classes and track student progress</p>
        </div>
        {/* Statistics Section */}
        <div className="stats-container">
          <div className="stat-card">
            <div className="stat-number">{dashboardData.stats.totalClasses}</div>
            <div className="stat-label">Total Classes</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{dashboardData.stats.totalStudents}</div>
            <div className="stat-label">Total Students</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{dashboardData.stats.classesWithTeachers}</div>
            <div className="stat-label">Active Classes</div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="dashboard-controls">
        <div className="search-section">
          <input
            type="text"
            placeholder="Search classes by name or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filter-section">
          <select
            value={filterDepartment}
            onChange={(e) => setFilterDepartment(e.target.value)}
            className="department-filter"
          >
            {departments.map(dept => (
              <option key={dept} value={dept}>{dept}</option>
            ))}
          </select>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="sort-filter"
          >
            <option value="name">Sort by Name</option>
            <option value="student_count">Sort by Student Count</option>
            <option value="class_id">Sort by Class ID</option>
          </select>
        </div>
      </div>

      {/* Classes Grid */}
      <div className="classes-dashboard-grid">
        {filteredClasses.map((classData) => (
          <div 
            key={classData.id || classData.class_uuid} 
            className="dashboard-class-card"
            onClick={() => handleClassClick(classData)}
          >
            <div className="card-header">
              <h3 className="class-name">{classData.name || classData.class_name || 'Unknown Class'}</h3>
              <span className="class-id">{classData.class_id || 'No ID'}</span>
            </div>

            <div className="card-stats">
              <div className="stat-item">
                <span className="stat-icon">👥</span>
                <span className="stat-value">{classData.student_count || 0}</span>
                <span className="stat-label">學生</span>
              </div>
            </div>

            <div className="card-teachers">
              <div className="teacher-row">
                <span className="teacher-label">班主任:</span>
                <span className="teacher-name">
                  {classData.class_master?.name || '未安排'}
                </span>
                <span className="teacher-dept">
                  {classData.class_master?.department || ''}
                </span>
              </div>
              <div className="teacher-row">
                <span className="teacher-label">副班主任:</span>
                <span className="teacher-name">
                  {classData.class_vice_master?.name || '未安排'}
                </span>
                <span className="teacher-dept">
                  {classData.class_vice_master?.department || ''}
                </span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="action-buttons">
              <button className="action-btn attendance" onClick={handleAttendanceClick}>
                <i className="fas fa-chart-bar"></i>
                Attendance
              </button>
              <button className="action-btn payment" onClick={handlePaymentClick}>
                <i className="fas fa-money-bill-wave"></i>
                Payment
              </button>
              <button className="action-btn activities" onClick={handleSchoolActivitiesClick}>
                <i className="fas fa-calendar-alt"></i>
                Activities
              </button>
              <button className="action-btn eca" onClick={handleECAClick}>
                <i className="fas fa-users"></i>
                ECA
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredClasses.length === 0 && (
        <div className="no-results">
          <p>No classes found matching your criteria</p>
          <button onClick={() => {
            setSearchTerm('');
            setFilterDepartment('All');
          }}>Clear Filters</button>
        </div>
      )}
    </div>
  );
};

// Class Detail View Component
const ClassDetailView = ({ classData, onBack, onViewStudents }) => {
  const [classDetails, setClassDetails] = useState({
    studentCount: 0,
    classMaster: null,
    viceClassMaster: null,
    loading: true,
    error: null
  });

  useEffect(() => {
    fetchClassDetails();
  }, [classData]);

  const fetchClassDetails = async () => {
    try {
      setClassDetails(prev => ({ ...prev, loading: true }));
      
      // Fetch student count for this class
      const { data: studentsData, error: studentsError } = await supabaseAdmin
        .from('saas_students')
        .select('id')
        .eq('class_id', classData.class_id);

      if (studentsError) {
        throw new Error(`Failed to fetch students: ${studentsError.message}`);
      }

      // Fetch teacher assignments for this class from saas_teachers table
      const { data: classMasterData, error: classMasterError } = await supabaseAdmin
        .from('saas_teachers')
        .select('id, name, english_name, department, position')
        .eq('class_master', classData.id);

      const { data: viceMasterData, error: viceMasterError } = await supabaseAdmin
        .from('saas_teachers')
        .select('id, name, english_name, department, position')
        .eq('class_vice_master', classData.id);

      if (classMasterError) {
        console.warn('Failed to fetch class master:', classMasterError.message);
      }

      if (viceMasterError) {
        console.warn('Failed to fetch vice master:', viceMasterError.message);
      }

      // Process teacher data
      const classMaster = classMasterData && classMasterData.length > 0 ? classMasterData[0] : null;
      const viceClassMaster = viceMasterData && viceMasterData.length > 0 ? viceMasterData[0] : null;

      console.log('🔍 Debug: Processed teacher data - Class Master:', classMaster, 'Vice Class Master:', viceClassMaster);

      setClassDetails({
        studentCount: studentsData?.length || 0,
        classMaster,
        viceClassMaster,
        loading: false,
        error: null
      });

    } catch (err) {
      console.error('🔍 Debug: Error in fetchClassDetails:', err);
      setClassDetails({
        studentCount: 0,
        classMaster: null,
        viceClassMaster: null,
        loading: false,
        error: err.message
      });
    }
  };

  if (classDetails.loading) {
    return (
      <div className="class-detail-view">
        <div className="detail-header">
          <button className="back-btn" onClick={onBack}>
            ← 返回儀表板
          </button>
          <div className="class-info">
            <h1>{classData.name || classData.class_name || 'Unknown Class'} - 班別詳情</h1>
            <p className="class-id">{classData.class_id || 'No ID'}</p>
          </div>
        </div>
        <div className="detail-content">
          <div className="loading-spinner"></div>
          <p>載入班別資料中...</p>
        </div>
      </div>
    );
  }

  if (classDetails.error) {
    return (
      <div className="class-detail-view">
        <div className="detail-header">
          <button className="back-btn" onClick={onBack}>
            ← 返回儀表板
          </button>
          <div className="class-info">
            <h1>{classData.name || classData.class_name || 'Unknown Class'} - 班別詳情</h1>
            <p className="class-id">{classData.class_id || 'No ID'}</p>
          </div>
        </div>
        <div className="detail-content">
          <div className="error-message">
            <p>錯誤: {classDetails.error}</p>
            <button onClick={fetchClassDetails}>重試</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="class-detail-view">
      <div className="detail-header">
        <button className="back-btn" onClick={onBack}>
          ← 返回儀表板
        </button>
        <div className="class-info">
          <h1>{classData.name || classData.class_name || 'Unknown Class'} - 班別詳情</h1>
          <p className="class-id">{classData.class_id || 'No ID'}</p>
        </div>
      </div>

      <div className="detail-content">
        <div className="detail-grid">
          <div className="detail-card">
            <h3>班別統計</h3>
            <div className="stat-display">
              <span className="stat-number">{classDetails.studentCount}</span>
              <span className="stat-label">名學生</span>
            </div>
          </div>

          <div className="detail-card">
            <h3>班主任</h3>
            {classDetails.classMaster ? (
              <div className="teacher-info">
                <span className="teacher-name">{classDetails.classMaster.name}</span>
                <span className="teacher-dept">{classDetails.classMaster.department}</span>
                <span className="teacher-pos">{classDetails.classMaster.position}</span>
              </div>
            ) : (
              <span className="no-data">未安排</span>
            )}
          </div>

          <div className="detail-card">
            <h3>副班主任</h3>
            {classDetails.viceClassMaster ? (
              <div className="teacher-info">
                <span className="teacher-name">{classDetails.viceClassMaster.name}</span>
                <span className="teacher-dept">{classDetails.viceClassMaster.department}</span>
                <span className="teacher-pos">{classDetails.viceClassMaster.position}</span>
              </div>
            ) : (
              <span className="no-data">未安排</span>
            )}
          </div>
        </div>

        <div className="action-buttons">
          <button className="primary-btn" onClick={onViewStudents}>
            查看學生名單 ({classDetails.studentCount})
          </button>
        </div>
      </div>
    </div>
  );
};

// Student Detail View Component
const StudentDetailView = ({ classData, onBack, tenantId }) => {
  console.log('StudentDetailView component loaded - PDF fix version 2.0');
  const [students, setStudents] = useState([]);
  const [studentECAs, setStudentECAs] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchClassStudents();
  }, [classData.class_id, tenantId]);

  const fetchClassStudents = async () => {
    try {
      setLoading(true);
      
      // Try direct table query first instead of RPC
      const { data: directStudents, error: directError } = await supabaseAdmin
        .from('saas_students')
        .select('*')
        .eq('class_id', classData.class_id)
        .eq('tenant_id', tenantId);
      
      if (directError) {
        throw directError;
      }
      
      if (directStudents && directStudents.length > 0) {
        setStudents(directStudents);
        
        // NO MORE fetchStudentECAs call - this was causing the infinite loop
        setStudentECAs({}); // Set empty ECA state initially
      } else {
        
        // Fallback to RPC function
        const { data, error } = await supabaseAdmin.rpc('get_class_students', {
          p_class_id: classData.class_id,
          p_tenant_id: tenantId
        });

        if (error) throw error;

        if (data && data.status === 'success') {
          const studentsList = data.students || [];
          setStudents(studentsList);
          
          // NO MORE fetchStudentECAs call here either
          setStudentECAs({}); // Set empty ECA state initially
        } else {
          throw new Error('No students data or unsuccessful status from RPC');
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Function to create sample ECA data if none exists
  const createSampleECAData = async (studentsList) => {
    try {
      if (!studentsList || studentsList.length === 0) {
        alert('❌ No students in current class to create ECA data for');
        return;
      }

      // First, check if ECA categories and activities exist
      const { data: existingCategories, error: categoriesError } = await supabaseAdmin
        .from('eca_categories')
        .select('*');

      if (categoriesError) {
        alert(`❌ Error checking categories: ${categoriesError.message}`);
        return;
      }

      // Create categories if none exist
      let categories = existingCategories;
      if (!categories || categories.length === 0) {
        const sampleCategories = [
          { name: '體育運動', name_en: 'Sports', color_code: '#ef4444', description: 'Physical activities and sports' },
          { name: '學術競賽', name_en: 'Academic', color_code: '#3b82f6', description: 'Academic competitions and clubs' },
          { name: '藝術文化', name_en: 'Arts & Culture', color_code: '#8b5cf6', description: 'Arts, music, and cultural activities' },
          { name: '服務學習', name_en: 'Service Learning', color_code: '#10b981', description: 'Community service and leadership' },
          { name: '科技創新', name_en: 'Technology', color_code: '#f59e0b', description: 'Technology and innovation clubs' }
        ];

        const { data: newCategories, error: createCategoriesError } = await supabaseAdmin
          .from('eca_categories')
          .insert(sampleCategories)
          .select();

        if (createCategoriesError) {
          alert(`❌ Error creating categories: ${createCategoriesError.message}`);
          return;
        }

        categories = newCategories;
      }

      // Check if ECA activities exist
      const { data: existingActivities, error: activitiesError } = await supabaseAdmin
        .from('eca_activities')
        .select('*');

      if (activitiesError) {
        alert(`❌ Error checking activities: ${activitiesError.message}`);
        return;
      }

      // Create activities if none exist
      let activities = existingActivities;
      if (!activities || activities.length === 0) {
        const sampleActivities = [
          { name: '籃球隊', name_en: 'Basketball Team', category_id: categories[0].id, max_capacity: 25, fee: 0, description: 'School basketball team' },
          { name: '足球隊', name_en: 'Football Team', category_id: categories[0].id, max_capacity: 25, fee: 0, description: 'School football team' },
          { name: '辯論社', name_en: 'Debate Club', category_id: categories[1].id, max_capacity: 20, fee: 50, description: 'Academic debate club' },
          { name: '科學社', name_en: 'Science Club', category_id: categories[1].id, max_capacity: 30, fee: 30, description: 'Science and research club' },
          { name: '合唱團', name_en: 'Choir', category_id: categories[2].id, max_capacity: 40, fee: 0, description: 'School choir' },
          { name: '美術社', name_en: 'Art Club', category_id: categories[2].id, max_capacity: 25, fee: 100, description: 'Art and drawing club' },
          { name: '學生會', name_en: 'Student Council', category_id: categories[3].id, max_capacity: 15, fee: 0, description: 'Student government' },
          { name: '環保社', name_en: 'Environmental Club', category_id: categories[3].id, max_capacity: 20, fee: 0, description: 'Environmental awareness club' },
          { name: '編程社', name_en: 'Coding Club', category_id: categories[4].id, max_capacity: 25, fee: 80, description: 'Programming and coding club' }
        ];

        // Add tenant_id to each activity (use the first student's tenant_id)
        const tenantId = studentsList[0]?.tenant_id;
        sampleActivities.forEach(activity => {
          activity.tenant_id = tenantId;
        });

        const { data: newActivities, error: createActivitiesError } = await supabaseAdmin
          .from('eca_activities')
          .insert(sampleActivities)
          .select();

        if (createActivitiesError) {
          alert(`❌ Error creating activities: ${createActivitiesError.message}`);
          return;
        }

        activities = newActivities;
      }

      // Now create memberships SPECIFICALLY for current class students
      // Clear any existing memberships for these students first
      const currentStudentIds = studentsList.map(s => s.id);
      await supabaseAdmin
        .from('eca_student_memberships')
        .delete()
        .in('student_id', currentStudentIds);

      // Create new memberships for current class students
      const membershipsToCreate = [];
      
      studentsList.forEach((student, studentIndex) => {
        // Each student joins 1-3 random activities
        const numActivities = Math.floor(Math.random() * 3) + 1;
        const selectedActivities = activities.sort(() => 0.5 - Math.random()).slice(0, numActivities);
        
        selectedActivities.forEach(activity => {
          membershipsToCreate.push({
            eca_id: activity.id,
            student_id: student.id,
            role: 'member',
            join_date: new Date(Date.now() - Math.random() * 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Random date within last year
            status: 'enrolled',
            payment_status: activity.fee > 0 ? (Math.random() > 0.8 ? 'pending' : 'paid') : 'free'
          });
        });
      });

      if (membershipsToCreate.length > 0) {
        const { data: newMemberships, error: createMembershipsError } = await supabaseAdmin
          .from('eca_student_memberships')
          .insert(membershipsToCreate)
          .select();

        if (createMembershipsError) {
          alert(`❌ Error creating memberships: ${createMembershipsError.message}`);
          return;
        }

        // Update current_enrollment in activities
        for (const activity of activities) {
          const activityMemberships = newMemberships.filter(m => m.eca_id === activity.id);
          if (activityMemberships.length > 0) {
            await supabaseAdmin
              .from('eca_activities')
              .update({ current_enrollment: activityMemberships.length })
              .eq('id', activity.id);
          }
        }

        alert(`✅ Successfully created ECA data for current class!
        
Created ${newMemberships?.length || 0} memberships for ${studentsList.length} students
Activities: ${activities.length}
Categories: ${categories.length}

Now click "刷新課外活動" to see the ECA data on student cards!`);
        
        // Automatically refresh ECA data after creation
        refreshECAData();
        
      } else {
        alert('❌ No memberships were created');
      }

    } catch (err) {
      alert(`❌ Error creating sample ECA data: ${err.message}`);
    }
  };

  // Function to download student list as PDF
  const downloadStudentList = async () => {
    try {
      if (!students || students.length === 0) {
        alert('沒有學生資料可供下載');
        return;
      }

      // Import html2pdf dynamically
      const html2pdf = (await import('html2pdf.js')).default;

      // Create HTML content with proper Chinese font support
      const exportDate = new Date().toLocaleDateString('en-HK');
      
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <style>
            body {
              font-family: 'Microsoft YaHei', 'SimHei', 'Arial Unicode MS', 'Noto Sans CJK SC', sans-serif;
              margin: 0;
              padding: 20px;
              background: white;
              font-size: 12px;
            }
            .header {
              text-align: center;
              margin-bottom: 20px;
            }
            .school-name {
              font-size: 24px;
              font-weight: bold;
              margin-bottom: 5px;
              color: #333;
            }
            .school-subtitle {
              font-size: 16px;
              margin-bottom: 10px;
              color: #666;
            }
            .title {
              font-size: 20px;
              font-weight: bold;
              margin-bottom: 15px;
              color: #333;
            }
            .class-info {
              font-size: 14px;
              margin-bottom: 20px;
              text-align: left;
              color: #555;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 20px;
              background: white;
            }
            th {
              background-color: #4285f4;
              color: white;
              padding: 8px;
              text-align: left;
              font-weight: bold;
              border: 1px solid #ddd;
              font-size: 11px;
            }
            td {
              padding: 6px 8px;
              border: 1px solid #ddd;
              text-align: left;
              font-size: 10px;
              color: #333;
            }
            tr:nth-child(even) {
              background-color: #f5f5f5;
            }
            tr:nth-child(odd) {
              background-color: white;
            }
            .footer {
              font-size: 10px;
              color: #666;
              text-align: center;
              margin-top: 20px;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="school-name">MyClass</div>
            <div class="school-subtitle">Real School A</div>
            <div class="title">Class Student List</div>
            <div class="class-info">
              <div>Class: ${classData.name}</div>
              <div>Class Code: ${classData.class_id}</div>
              <div>Export Date: ${exportDate}</div>
            </div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>Class</th>
                <th>No</th>
                <th>Student ID</th>
                <th>English Name</th>
                <th>Chinese Name</th>
              </tr>
            </thead>
            <tbody>
              ${students.map((student, index) => `
                <tr>
                  <td>${classData.name}</td>
                  <td>${index + 1}</td>
                  <td>${student.student_id || 'N/A'}</td>
                  <td>${student.english_name || 'N/A'}</td>
                  <td>${student.name || 'N/A'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          <div class="footer">
            Generated by MyClass Parent App | Total Students: ${students.length}
          </div>
        </body>
        </html>
      `;

      // Create a hidden iframe to properly render the HTML
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.left = '-9999px';
      iframe.style.top = '-9999px';
      iframe.style.width = '210mm';
      iframe.style.height = '297mm';
      iframe.style.border = 'none';
      document.body.appendChild(iframe);

      // Write HTML content to iframe
      iframe.contentDocument.open();
      iframe.contentDocument.write(htmlContent);
      iframe.contentDocument.close();

      // Wait for iframe to load and render
      await new Promise((resolve) => {
        iframe.onload = resolve;
        setTimeout(resolve, 1000); // Fallback timeout
      });

      // Get the body element from iframe
      const element = iframe.contentDocument.body;

      // Configure PDF options for better Chinese character support
      const options = {
        margin: [10, 10, 10, 10],
        filename: `ClassStudentList_${classData.name}_${new Date().toISOString().slice(0, 10)}.pdf`,
        image: { 
          type: 'jpeg', 
          quality: 0.98 
        },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          letterRendering: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          width: 794, // A4 width in pixels at 96 DPI
          height: 1123 // A4 height in pixels at 96 DPI
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'portrait',
          compress: true
        }
      };

      // Generate and download PDF directly
      await html2pdf().set(options).from(element).save();
      
      // Clean up
      document.body.removeChild(iframe);

      console.log('PDF downloaded successfully');
    } catch (error) {
      console.error('Error downloading student list:', error);
      alert('下載學生名單時發生錯誤: ' + error.message);
    }
  };

  // Function to manually refresh ECA data
  const refreshECAData = async () => {
    try {
      // Get all ECA memberships for enrolled students
      const { data: allMemberships, error: allMembershipsError } = await supabaseAdmin
        .from('eca_student_memberships')
        .select('*')
        .eq('status', 'enrolled');

      if (allMembershipsError) {
        alert(`❌ Error fetching memberships: ${allMembershipsError.message}`);
        return;
      }

      // Get ECA activities and categories
      const { data: allActivities, error: activitiesError } = await supabaseAdmin
        .from('eca_activities')
        .select('*');

      const { data: allCategories, error: categoriesError } = await supabaseAdmin
        .from('eca_categories')
        .select('*');

      if (activitiesError || categoriesError) {
        alert(`❌ Error fetching ECA data: ${activitiesError?.message || categoriesError?.message}`);
        return;
      }

      // Build ECA data manually
      const ecaByStudent = {};
      
      if (allMemberships && allMemberships.length > 0) {
        allMemberships.forEach(membership => {
          const studentId = membership.student_id;
          const ecaId = membership.eca_id;
          
          // Find the ECA activity
          const activity = allActivities?.find(a => a.id === ecaId);
          // Find the category
          const category = allCategories?.find(c => c.id === activity?.category_id);
          
          if (activity && category) {
            if (!ecaByStudent[studentId]) {
              ecaByStudent[studentId] = [];
            }
            
            const ecaInfo = {
              name: activity.name || 'Unknown Activity',
              nameEn: activity.name_en || '',
              category: category.name || 'Unknown Category',
              categoryEn: category.name_en || '',
              colorCode: category.color_code || '#8b5cf6'
            };
            
            ecaByStudent[studentId].push(ecaInfo);
          }
        });
      }

      setStudentECAs(ecaByStudent);
      
      if (Object.keys(ecaByStudent).length > 0) {
        alert(`🎯 ECA data refreshed: ${Object.keys(ecaByStudent).length} students have ECA data`);
      } else {
        alert(`⚠️ No ECA data found for current class students. Found ${allMemberships?.length || 0} total memberships, but none match the ${students.length} students in this class.`);
      }
      
    } catch (err) {
      alert(`❌ Error refreshing ECA data: ${err.message}`);
    }
  };

  // Function to check all ECA data across all students
  const checkAllECAData = async () => {
    try {
      // Get all ECA memberships
      const { data: allMemberships, error: membershipsError } = await supabaseAdmin
        .from('eca_student_memberships')
        .select('*');

      if (membershipsError) {
        alert(`❌ Error: ${membershipsError.message}`);
        return;
      }

      if (allMemberships && allMemberships.length > 0) {
        // Get all students to see which ones have ECA data
        const { data: allStudents, error: studentsError } = await supabaseAdmin
          .from('saas_students')
          .select('id, name, class_id, tenant_id');

        if (studentsError) {
          alert(`❌ Error fetching students: ${studentsError.message}`);
          return;
        }

        // Check which students have ECA data
        const studentsWithECAs = new Set(allMemberships.map(m => m.student_id));
        const studentsWithoutECAs = allStudents?.filter(s => !studentsWithECAs.has(s.id)) || [];
        
        alert(`🔍 ECA Data Analysis:
        
Total ECA memberships: ${allMemberships?.length || 0}
Total students: ${allStudents?.length || 0}
Students with ECAs: ${studentsWithECAs.size}
Students without ECAs: ${studentsWithoutECAs.length}

Current class (${students.length} students):
- With ECAs: ${students.length - studentsWithoutECAs.length}
- Without ECAs: ${studentsWithoutECAs.length}

Check console for detailed breakdown.`);
        
      } else {
        alert('❌ No ECA memberships found in the database at all.');
      }
      
    } catch (err) {
      alert(`❌ Comprehensive check error: ${err.message}`);
    }
  };

  // Simple diagnostic function to show ECA data mismatch
  const diagnoseECAMismatch = async () => {
    try {
      // Get all ECA memberships
      const { data: allMemberships, error: membershipsError } = await supabaseAdmin
        .from('eca_student_memberships')
        .select('*');

      if (membershipsError) {
        alert(`❌ Error: ${membershipsError.message}`);
        return;
      }

      if (allMemberships && allMemberships.length > 0) {
        // Get student details for each membership
        const studentIdsWithECAs = [...new Set(allMemberships.map(m => m.student_id))];
        
        // Get details for these students
        const { data: studentsWithECAs, error: studentsError } = await supabaseAdmin
          .from('saas_students')
          .select('id, name, class_id, tenant_id')
          .in('id', studentIdsWithECAs);

        if (studentsError) {
          alert(`❌ Error fetching students: ${studentsError.message}`);
          return;
        }

        // Check for mismatches
        const currentClassStudentIds = students.map(s => s.id);
        const currentClassWithECAs = studentsWithECAs?.filter(s => currentClassStudentIds.includes(s.id)) || [];
        const otherClassWithECAs = studentsWithECAs?.filter(s => !currentClassStudentIds.includes(s.id)) || [];
        
        // Show the mismatch details
        let alertMessage = `🔍 ECA Data Mismatch Diagnosis:
        
Total ECA memberships: ${allMemberships?.length || 0}
Students with ECAs: ${studentsWithECAs?.length || 0}

Current class (${classData.name || classData.class_name || 'Unknown Class'}):
- Students: ${students.length}
- Students with ECAs: ${currentClassWithECAs.length}
- Students without ECAs: ${students.length - currentClassWithECAs.length}

Mismatch found: ${otherClassWithECAs.length} students in OTHER classes have ECA data

Check console for detailed breakdown.`;

        if (otherClassWithECAs.length > 0) {
          alertMessage += `\n\nStudents with ECAs in OTHER classes:
${otherClassWithECAs.slice(0, 5).map(s => `- ${s.name} (Class: ${s.class_id})`).join('\n')}`;
        }
        
        alert(alertMessage);
        
      } else {
        alert('❌ No ECA memberships found in the database.');
      }
      
    } catch (err) {
      alert(`❌ Diagnosis error: ${err.message}`);
    }
  };

  if (loading) {
    return (
      <div className="student-loading">
        <div className="loading-spinner"></div>
        <p>載入學生資料中...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="student-error">
        <p>錯誤: {error}</p>
        <button onClick={fetchClassStudents}>重試</button>
      </div>
    );
  }

  return (
    <div className="student-detail-view">
      <div className="detail-header">
        <button className="back-btn" onClick={onBack}>
          ← 返回班別詳情
        </button>
        <div className="class-info">
          <h1>{classData.name || classData.class_name || 'Unknown Class'} - 學生名單</h1>
          <p className="class-id">{classData.class_id || 'No ID'}</p>
        </div>
      </div>

      <div className="students-content">
        <div className="students-header">
          <h3>班別學生 ({students.length})</h3>
          <div className="student-actions">
            <button 
              className="refresh-btn" 
              onClick={() => fetchClassStudents()}
              style={{
                background: '#8b5cf6',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '8px',
                cursor: 'pointer',
                marginRight: '12px'
              }}
            >
              刷新學生資料
            </button>
            <button 
              className="download-btn" 
              onClick={() => downloadStudentList()}
              style={{
                background: '#dc2626',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '8px',
                cursor: 'pointer',
                marginRight: '12px'
              }}
              title="下載學生名單 (PDF格式)"
            >
              📄 下載學生名單
            </button>
            <button 
              className="eca-refresh-btn" 
              onClick={() => refreshECAData()}
              style={{
                background: '#06b6d4',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '8px',
                cursor: 'pointer',
                marginRight: '12px'
              }}
            >
              刷新課外活動
            </button>
            <button 
              className="create-eca-btn" 
              onClick={() => createSampleECAData(students)}
              style={{
                background: '#10b981',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '8px',
                cursor: 'pointer',
                marginRight: '12px'
              }}
            >
              創建示例課外活動數據
            </button>
            <button 
              className="test-btn" 
              onClick={() => {
                // Silent test - no console output in production
              }}
              style={{
                background: '#f59e0b',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              測試狀態
            </button>
            <button 
              className="check-all-eca-btn" 
              onClick={() => checkAllECAData()}
              style={{
                background: '#4f46e5',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              檢查所有課外活動數據
            </button>
            <button 
              className="diagnose-eca-btn" 
              onClick={() => diagnoseECAMismatch()}
              style={{
                background: '#ef4444',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              診斷課外活動數據不匹配
            </button>
          </div>
        </div>

        {/* ECA Participation Summary */}
        {Object.keys(studentECAs).length > 0 && (
          <div className="eca-summary">
            <div className="eca-summary-header">
              <h4>課外活動參與概覽</h4>
              <div className="eca-summary-stats">
                <div className="eca-stat">
                  <span className="stat-number">
                    {Object.values(studentECAs).filter(ecas => ecas.length > 0).length}
                  </span>
                  <span className="stat-label">參與學生</span>
                </div>
                <div className="eca-stat">
                  <span className="stat-number">
                    {Object.values(studentECAs).reduce((total, ecas) => total + ecas.length, 0)}
                  </span>
                  <span className="stat-label">總參與次數</span>
                </div>
                <div className="eca-stat">
                  <span className="stat-number">
                    {Math.round((Object.values(studentECAs).filter(ecas => ecas.length > 0).length / students.length) * 100)}%
                  </span>
                  <span className="stat-label">參與率</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="students-grid">
          {students.map((student, index) => {
            const studentECAList = studentECAs[student.id] || [];
            
            return (
              <div key={student.id} className="student-card">
                <div className="student-avatar">
                  {student.name?.charAt(0) || 'S'}
                </div>
                <div className="student-info">
                  <span className="student-id">{student.student_id}</span>
                  <span className="student-name">{student.name}</span>
                  <span className="student-code">{student.student_code}</span>
                  
                  {/* ECA Information */}
                  {studentECAList.length > 0 && (
                    <div className="student-ecas">
                      <div className="eca-label">課外活動:</div>
                      <div className="eca-tags">
                        {studentECAList.slice(0, 3).map((eca, ecaIndex) => (
                          <span 
                            key={ecaIndex} 
                            className="eca-tag"
                            style={{
                              backgroundColor: eca.colorCode || '#8b5cf6',
                              color: 'white'
                            }}
                            title={`${eca.name} (${eca.category})`}
                          >
                            {eca.name}
                          </span>
                        ))}
                        {studentECAList.length > 3 && (
                          <span className="eca-more" title={`還有 ${studentECAList.length - 3} 個活動`}>
                            +{studentECAList.length - 3}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {studentECAList.length === 0 && (
                    <div className="student-ecas">
                      <div className="eca-label">課外活動:</div>
                      <span className="no-eca">暫未參加</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {students.length === 0 && (
          <div className="no-students">
            <p>此班別目前沒有學生</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComprehensiveClassroomDashboard;